import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

const BankAccountForm = ({ 
  editingBank, 
  onSave, 
  onCancel, 
  loading, 
  token 
}) => {
  const [formData, setFormData] = useState({
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    branch: '',
    isDefault: false
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [validating, setValidating] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [ifscData, setIfscData] = useState(null);

  // API configuration
  const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  // Initialize form with editing data if provided
  useEffect(() => {
    if (editingBank) {
      setFormData({
        accountHolderName: editingBank.accountHolderName || '',
        bankName: editingBank.bankName || '',
        accountNumber: editingBank.accountNumber || '',
        ifscCode: editingBank.ifscCode || '',
        branch: editingBank.branch || '',
        isDefault: editingBank.isDefault || false
      });
    } else {
      // Reset form when not editing
      setFormData({
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        branch: '',
        isDefault: false
      });
    }
    
    // Load popular banks list for autocomplete
    setBankList([
      'State Bank of India',
      'HDFC Bank',
      'ICICI Bank',
      'Axis Bank',
      'Kotak Mahindra Bank',
      'Bank of Baroda',
      'Punjab National Bank',
      'Canara Bank',
      'Yes Bank',
      'IDBI Bank'
    ]);
    
    // Clear any existing validation data
    setIfscData(null);
    setErrors({});
    setSuccess('');
  }, [editingBank]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Clear errors when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account holder name is required';
    }
    
    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }
    
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber.replace(/\s/g, ''))) {
      newErrors.accountNumber = 'Account number must be 9-18 digits';
    }
    
    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
      newErrors.ifscCode = 'Invalid IFSC code format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate bank details using IFSC code
  const validateBankDetails = async () => {
    if (!formData.ifscCode) {
      setErrors({...errors, ifscCode: 'Please enter IFSC code to validate'});
      return false;
    }

    // Validate IFSC format first
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
      setErrors({...errors, ifscCode: 'Invalid IFSC code format'});
      return false;
    }

    try {
      setValidating(true);
      const response = await axios.get(`https://ifsc.razorpay.com/${formData.ifscCode}`);
      
      if (response.status === 200 && response.data) {
        const bankData = response.data;
        setIfscData(bankData);
        
        // Auto-fill bank name and branch if empty
        const updates = {};
        
        if (!formData.bankName && bankData.BANK) {
          updates.bankName = bankData.BANK;
        }
        
        if (!formData.branch && bankData.BRANCH) {
          updates.branch = bankData.BRANCH;
        }
        
        if (Object.keys(updates).length > 0) {
          setFormData(prev => ({ ...prev, ...updates }));
        }
        
        setSuccess('Bank details validated successfully!');
        setTimeout(() => setSuccess(''), 3000);
        return true;
      }
    } catch (err) {
      console.error('Error validating IFSC:', err);
      if (err.response && err.response.status === 404) {
        setErrors({...errors, ifscCode: 'Invalid IFSC code. Please check and try again.'});
      } else {
        setErrors({...errors, ifscCode: 'Failed to validate IFSC code. Please try again.'});
      }
      return false;
    } finally {
      setValidating(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) return;
    
    // Validate bank details if not editing or if IFSC was changed
    if (!editingBank || editingBank.ifscCode !== formData.ifscCode) {
      const isValid = await validateBankDetails();
      if (!isValid) return;
    }
    
    // Call parent's save function
    onSave(formData);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
      <h2 className="text-xl font-sem极bold text-gray-800 mb-6">
        {editingBank ? 'Edit Bank Account' : 'Add New Bank Account'}
      </h2>
      
      {/* Status Messages */}
      {success && (
        <div className="mb-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4极" clipRule="evenodd" />
          </svg>
          <span>{success}</span>
        </div>
      )}

      {/* Add/Edit Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700">
              Account Holder Name *
            </label>
            <input
              type="text"
              name="accountHolderName"
              id= "accountHolderName"
              value={formData.accountHolderName}
              onChange={handleInputChange}
              className={`mt-1 block w-full border ${errors.accountHolderName ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
            />
            {errors.accountHolderName && (
              <p className="mt-1 text-sm text-red-600">{errors.accountHolderName}</p>
            )}
          </div>

          <div>
            <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
              Bank Name *
            </label>
            <input
              type="text"
              name="bankName"
              id="bankName"
              list="bankList"
              value={formData.bankName}
              onChange={handleInputChange}
              className={`mt-1 block w-full border ${errors.bankName ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
            />
            <datalist id="bankList">
              {bankList.map((bank, index) => (
                <option key={index} value={bank} />
              ))}
            </datalist>
            {errors.bankName && (
              <p className="mt-1 text-sm text-red-600">{errors.bankName}</p>
            )}
          </div>

          <div>
            <label htmlFor="branch" className="block text-sm font-medium text-gray-700">
              Branch
            </label>
            <input
              type="text"
              name="branch"
              id="branch"
              value={formData.branch}
              onChange={handleInputChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
              Account Number *
            </label>
            <input
              type="text"
              name="accountNumber"
              id="accountNumber"
              value={formData.accountNumber}
              onChange={handleInputChange}
              className={`mt-1 block w-full border ${errors.accountNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
            />
            {errors.accountNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.accountNumber}</p>
            )}
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
                IFSC Code *
              </label>
              <input
                type="text"
                name="ifscCode"
                id="ifscCode"
                value={formData.ifscCode}
                onChange={handleInputChange}
                className={`mt-1 block w-full border ${errors.ifscCode ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
                placeholder="e.g., SBIN0000001"
              />
              {errors.ifscCode && (
                <p className="mt-1 text-sm text-red-600">{errors.ifscCode}</p>
              )}
              {ifscData && (
                <p className="mt-1 text-xs text-green-600">
                  Verified: {ifscData.BANK} ({ifscData.BRANCH})
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={validateBankDetails}
              disabled={validating || !formData.ifscCode}
              className="h-10 py-2 px-3 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {validating ? (
                <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/s极" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Validate'
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center">
          <input
            id="isDefault"
            name="isDefault"
            type="checkbox"
            checked={formData.isDefault}
            onChange={handleInputChange}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
            Set as default bank account
          </label>
        </div>

        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-75"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : editingBank ? 'Update' : 'Add Account'}
          </button>
        </div>
      </form>
    </div>
  );
};

// New component to display bank account details
const BankAccountDetails = ({ bank, onClose, onEdit, onSetDefault, onDelete }) => {
  // Format account number for display (mask most digits)
  const formatAccountNumber = (accNumber) => {
    if (!accNumber) return '';
    const lastFour = accNumber.slice(-4);
    return `****${lastFour}`;
  };

  // Bank logos mapping
  const bankLogos = {
    'hdfc': 'https://static.vecteezy.com/system/resources/previews/020/336/703/original/hdfc-logo-hdfc-icon-free-free-vector.jpg',
    'icici': 'https://d1i7580riw15wg.cloudfront.net/assets/newui/icici-9d1aebfd6e6615de405d5f1ed8abfa9974fa6b700c571ad75a2d8287efa853b0.webp',
    'sbi': 'https://www.freepnglogos.com/uploads/sbi-logo-png/sbi-logo-sbi-symbol-meaning-history-and-evolution-11.png',
    'axis': 'https://1.bp.blogspot.com/-TmSdLBZ1pJI/YCp88SdAmoI/AAAAAAAAB5g/dt1dTYBHVtkTRnM9JsSSWWkcFJ9myLf7wCLcBGAsYHQ/w1200-h630-p-k-no-nu/AXIS+BANK+PNG.png',
    'kotak': 'https://tse2.mm.bing.net/th/id/OIP.6UU_ux7jC5nANk53xhpu0wHaE8?r=0&w=600&h=400&rs=1&pid=ImgDetMain&o=7&rm=3',
    'yes': 'https://bfsi.eletsonline.com/wp-content/uploads/2023/05/YES-BANK.jpg',
    'pnb': 'https://static.vecteezy.com/system/resources/previews/020/336/282/non_2x/punjab-national-bank-pnb-bank-logo-free-free-vector.jpg',
    'bob': 'https://www.clipartmax.com/png/middle/215-2155663_bank-of-baroda-vector-logo-bank-of-baroda极ogo-clipart.png',
    'canara': 'https://www.liblogo.com/img-logo/ca8792c86d-canara-bank-logo-canara-bank-launches-qualified-institutional-placement.png',
    'idbi': 'https://www.companieshistory.com/wp-content/uploads/2013/07/IDBI-Bank.png',
    'default': 'https://static.vecteezy.com/system/resources/previews/013/948/672/large_2x/bank-icon-logo-design-vector.jpg'
  };

  // Get bank logo based on bank name
  const getBankLogo = (bankName) => {
    if (!bankName) return bankLogos.default;
    
    const bankKey = bankName.toLowerCase();
    for (const [key, logo] of Object.entries(bankLogos)) {
      if (bankKey.includes(key)) return logo;
    }
    
    return bankLogos.default;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Bank Account Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-6">
        {/* Bank Logo and Name */}
        <div className="flex items-center">
          <div className="bg-white p-2 rounded-xl mr-4 shadow-sm border flex items-center justify-center w-16 h-16">
            <img 
              src={getBankLogo(bank.bankName)} 
              alt={bank.bankName}
              className="max-h-10 max-w-10 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = bankLogos.default;
              }}
            />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">{bank.bankName}</h3>
            {bank.isDefault && (
              <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                Default Account
              </span>
            )}
          </div>
        </div>

        {/* Account Details */}
        <div className="bg-gray-50 rounded-xl p-5">
          <h4 className="text-sm font-medium text-gray-500 mb-4">ACCOUNT DETAILS</h4>
          
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-500">Account Holder Name</p>
              <p className="text-sm font-medium text-gray-900">{bank.accountHolderName}</p>
            </div>
            
            <div>
              <p className="text-xs text-gray-500">Account Number</p>
              <p className="text-sm font-medium text-gray-900">{formatAccountNumber(bank.accountNumber)}</p>
            </div>
            
            <div>
              <p className="text-xs text-gray-500">IFSC Code</p>
              <p className="text-sm font-medium text-gray-900">{bank.ifscCode}</p>
            </div>
            
            <div>
              <p className="text-xs text-gray-500">Branch</p>
              <p className="text-sm font-medium text-gray-900">{bank.branch || 'Not specified'}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          {!bank.isDefault && (
            <button
              onClick={() => onSetDefault(bank._id)}
              className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Set as Default
            </button>
          )}
          <button
            onClick={() => onEdit(bank)}
            className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Edit Account
          </button>
        </div>
      </div>
    </div>
  );
};

const MyBankAccount = () => {
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validating, setValidating] = useState(false);
  const [bankList, setBankList] = useState([]);

  const token = Cookies.get("ecom_user_token");

  // API configuration
  const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  // Fetch bank details
  const fetchBankDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get('api/buyer/bank');
      setBankDetails(response.data.bankDetails || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch bank details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankDetails();
    // Load popular banks list for autocomplete
    setBankList([
      'State Bank of India',
      'HDFC Bank',
      'ICICI Bank',
      'Axis Bank',
      'Kotak Mahindra Bank',
      'Bank of Baroda',
      'Punjab National Bank',
      'Canara Bank',
      'Yes Bank',
      'IDBI Bank'
    ]);
  }, []);

  // Reset view states
  const resetView = () => {
    setSelectedBank(null);
    setIsEditing(false);
    setIsAdding(false);
    setIsViewing(false);
    setError('');
    setSuccess('');
  };

  // Add new bank details
  const handleAddBank = async (formData) => {
    try {
      setLoading(true);
      await api.post('api/buyer/add-bank-details', formData);
      setSuccess('Bank details added successfully');
      resetView();
      fetchBankDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add bank details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update bank details
  const handleUpdateBank = async (formData) => {
    try {
      setLoading(true);
      await api.patch(`api/buyer/update-bank/${selectedBank._id}`, formData);
      setSuccess('Bank details updated successfully');
      resetView();
      fetchBankDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update bank details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Set bank as default
  const setAsDefault = async (bankId) => {
    try {
      setLoading(true);
      await api.patch(`api/buyer/update-bank/${bankId}`, { isDefault: true });
      setSuccess('Default bank updated successfully');
      resetView();
      fetchBankDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set default bank');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete bank account
  const deleteBankAccount = async (bankId) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
    try {
      setLoading(true);
      await api.delete(`api/buyer/delete-bank/${bankId}`);
      setSuccess('Bank account deleted successfully');
      resetView();
      fetchBankDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete bank account');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Prepare form for editing
  const setupEditForm = (bank) => {
    setSelectedBank(bank);
    setIsEditing(true);
    setIsAdding(false);
    setIsViewing(false);
  };

  // View bank details
  const viewBankDetails = (bank) => {
    setSelectedBank(bank);
    setIsViewing(true);
    setIsEditing(false);
    setIsAdding(false);
  };

  // Start adding new bank
  const startAdding = () => {
    resetView();
    setIsAdding(true);
  };

  // Format account number for display (mask most digits)
  const formatAccountNumber = (accNumber) => {
    if (!accNumber) return '';
    const lastFour = accNumber.slice(-4);
    return `****${lastFour}`;
  };

  // Bank logos mapping
  const bankLogos = {
    'hdfc': 'https://static.vecteezy.com/system/resources/previews/020/336/703/original/hdfc-logo-hdfc-icon-free-free-vector.jpg',
    'icici': 'https://d1i7580riw15wg.cloudfront.net/assets/newui/icici-9d1aebfd6e6615de405d5f1ed8abfa9974fa6b700c571ad75a2d8287efa853b0.webp',
    'sbi': 'https://www.freepnglogos.com/uploads/sbi-logo-png/sbi-logo-sbi-symbol-meaning-history-and-evolution-11.png',
    'axis': 'https://1.bp.blogspot.com/-TmSdLBZ1pJI/YCp88SdAmoI/AAAAAAAAB5g/dt1dTYBHVtkTRnM9JsSSWWkcFJ9myLf7wCLcBGAsYHQ/w1200-h630-p-k-no-nu/AXIS+BANK+PNG.png',
    'kotak': 'https://tse2.mm.bing.net/th/id/OIP.6UU_ux7j极5nANk53xhpu0wHaE8?r=0&w=600&h=400&rs=1&pid=ImgDetMain&o=7&rm=3',
    'yes': 'https://bfsi.eletsonline.com/wp-content/uploads/2023/05/YES-BANK.jpg',
    'pnb': 'https://static.vecteezy.com/system/resources/previews/020/336/282/non_2x/punjab-national-bank-pnb-bank-logo-free-free-vector.jpg',
    'bob': 'https://www.clipartmax.com/png/middle/215-2155663_bank-of-baroda-vector-logo-bank-of-baroda-logo-clipart.png',
    'canara': 'https://www.liblogo.com/img-logo/ca8792c86d-canara-bank-logo-canara-bank-launches-qualified-institutional-placement.png',
    'idbi': 'https://www.companieshistory.com/wp-content/uploads/极013/07/IDBI-Bank.png',
    'default': 'https://static.vecteezy.com/system/resources/previews/013/948/672/large_2x/bank-icon-logo-design-vector.jpg'
  };

  // Get bank logo based on bank name
  const getBankLogo = (bankName) => {
    if (!bankName) return bankLogos.default;
    
    const bankKey = bankName.toLowerCase();
    for (const [key, logo] of Object.entries(bankLogos)) {
      if (bankKey.includes(key)) return logo;
    }
    
    return bankLogos.default;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">My Bank Accounts</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Manage your bank accounts for seamless transactions. Your financial details are securely encrypted.
          </p>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Bank Accounts List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800">Your Bank Accounts</h2>
                  <button
                    onClick={startAdding}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center shadow-md"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add New Bank
                  </button>
                </div>

                {loading && !isAdding && !isEditing ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
                  </div>
                ) : bankDetails.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <div className="bg-indigo-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                      <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15极1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No bank accounts</h3>
                    <p className="mt-1 text-sm text-gray-500">Get started by adding your first bank account.</p>
                    <div className="mt-6">
                      <button
                        onClick={startAdding}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        + Add Bank Account
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bankDetails.map((bank) => (
                      <div 
                        key={bank._id} 
                        className={`p-5 border rounded-xl flex flex-col md:flex-row md:items-center justify-between transition-all duration-200 hover:shadow-md cursor-pointer ${bank.isDefault ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
                        onClick={() => viewBankDetails(bank)}
                      >
                        <div className="flex-1 flex items-center">
                          <div className="bg-white p-2 rounded-xl mr-4 shadow-sm border flex items-center justify-center w-12 h-12">
                            <img 
                              src={getBankLogo(bank.bankName)} 
                              alt={bank.bankName}
                              className="max-h-8 max-w-8 object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = bankLogos.default;
                              }}
                            />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{bank.bankName}</h3>
                            <p className="text-sm text-gray-500">{formatAccountNumber(bank.accountNumber)} • {bank.accountHolderName}</p>
                            {bank.isDefault && (
                              <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-4 md:mt-0 flex space-x-3">
                          {!bank.isDefault && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAsDefault(bank._id);
                              }}
                              className="text-sm text-indigo-600 hover:text-indigo-900 font-medium flex items-center"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setupEditForm(bank);
                            }}
                            className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Edit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Side Panel for Add/Edit/View */}
          <div className="lg:col-span-1">
            {isAdding && (
              <BankAccountForm
                onSave={handleAddBank}
                onCancel={resetView}
                loading={loading}
                token={token}
              />
            )}

            {isEditing && (
              <BankAccountForm
                editingBank={selectedBank}
                onSave={handleUpdateBank}
                onCancel={resetView}
                loading={loading}
                token={token}
              />
            )}

            {isViewing && (
              <BankAccountDetails
                bank={selectedBank}
                onClose={resetView}
                onEdit={setupEditForm}
                onSetDefault={setAsDefault}
                onDelete={deleteBankAccount}
              />
            )}

            {/* Information Card */}
            {!isAdding && !isEditing && !isViewing && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                  </svg>
                  Secure Banking
                </h3>
                <p className="text-gray-600 text-sm">
                  Your bank details are encrypted and stored securely. We never share your financial information with third parties.
                </p>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Bank-level encryption
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    PCI DSS compliant
                  </div>
                  <div className="flex items-center text-sm text-gray-500 mt-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                    </svg>
                    IFSC validation with Razorpay API
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBankAccount;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';

// const BankAccountForm = ({ 
//   editingBank, 
//   onSave, 
//   onCancel, 
//   loading, 
//   token 
// }) => {
//   const [formData, setFormData] = useState({
//     accountHolderName: '',
//     bankName: '',
//     accountNumber: '',
//     ifscCode: '',
//     branch: '',
//     isDefault: false
//   });
//   const [errors, setErrors] = useState({});
//   const [success, setSuccess] = useState('');
//   const [validating, setValidating] = useState(false);
//   const [bankList, setBankList] = useState([]);
//   const [ifscData, setIfscData] = useState(null);

//   // API configuration
//   const api = axios.create({
//     baseURL: process.env.REACT_APP_BACKEND_URL,
//     headers: {
//       'Authorization': `Bearer ${token}`
//     }
//   });

//   // Initialize form with editing data if provided
//   useEffect(() => {
//     if (editingBank) {
//       setFormData({
//         accountHolderName: editingBank.accountHolderName || '',
//         bankName: editingBank.bankName || '',
//         accountNumber: editingBank.accountNumber || '',
//         ifscCode: editingBank.ifscCode || '',
//         branch: editingBank.branch || '',
//         isDefault: editingBank.isDefault || false
//       });
//     } else {
//       // Reset form when not editing
//       setFormData({
//         accountHolderName: '',
//         bankName: '',
//         accountNumber: '',
//         ifscCode: '',
//         branch: '',
//         isDefault: false
//       });
//     }
    
//     // Load popular banks list for autocomplete
//     setBankList([
//       'State Bank of India',
//       'HDFC Bank',
//       'ICICI Bank',
//       'Axis Bank',
//       'Kotak Mahindra Bank',
//       'Bank of Baroda',
//       'Punjab National Bank',
//       'Canara Bank',
//       'Yes Bank',
//       'IDBI Bank'
//     ]);
    
//     // Clear any existing validation data
//     setIfscData(null);
//     setErrors({});
//     setSuccess('');
//   }, [editingBank]);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });

//     // Clear errors when user types
//     if (errors[name]) {
//       setErrors({
//         ...errors,
//         [name]: ''
//       });
//     }
//   };

//   // Validate form fields
//   const validateForm = () => {
//     const newErrors = {};
    
//     if (!formData.accountHolderName.trim()) {
//       newErrors.accountHolderName = 'Account holder name is required';
//     }
    
//     if (!formData.bankName.trim()) {
//       newErrors.bankName = 'Bank name is required';
//     }
    
//     if (!formData.accountNumber.trim()) {
//       newErrors.accountNumber = 'Account number is required';
//     } else if (!/^\d{9,18}$/.test(formData.accountNumber.replace(/\s/g, ''))) {
//       newErrors.accountNumber = 'Account number must be 9-18 digits';
//     }
    
//     if (!formData.ifscCode.trim()) {
//       newErrors.ifscCode = 'IFSC code is required';
//     } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
//       newErrors.ifscCode = 'Invalid IFSC code format';
//     }
    
//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // Validate bank details using IFSC code
//   const validateBankDetails = async () => {
//     if (!formData.ifscCode) {
//       setErrors({...errors, ifscCode: 'Please enter IFSC code to validate'});
//       return false;
//     }

//     // Validate IFSC format first
//     if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.toUpperCase())) {
//       setErrors({...errors, ifscCode: 'Invalid IFSC code format'});
//       return false;
//     }

//     try {
//       setValidating(true);
//       const response = await axios.get(`https://ifsc.razorpay.com/${formData.ifscCode}`);
      
//       if (response.status === 200 && response.data) {
//         const bankData = response.data;
//         setIfscData(bankData);
        
//         // Auto-fill bank name and branch if empty
//         const updates = {};
        
//         if (!formData.bankName && bankData.BANK) {
//           updates.bankName = bankData.BANK;
//         }
        
//         if (!formData.branch && bankData.BRANCH) {
//           updates.branch = bankData.BRANCH;
//         }
        
//         if (Object.keys(updates).length > 0) {
//           setFormData(prev => ({ ...prev, ...updates }));
//         }
        
//         setSuccess('Bank details validated successfully!');
//         setTimeout(() => setSuccess(''), 3000);
//         return true;
//       }
//     } catch (err) {
//       console.error('Error validating IFSC:', err);
//       if (err.response && err.response.status === 404) {
//         setErrors({...errors, ifscCode: 'Invalid IFSC code. Please check and try again.'});
//       } else {
//         setErrors({...errors, ifscCode: 'Failed to validate IFSC code. Please try again.'});
//       }
//       return false;
//     } finally {
//       setValidating(false);
//     }
//   };

//   // Handle form submission
//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     // Validate form first
//     if (!validateForm()) return;
    
//     // Validate bank details if not editing or if IFSC was changed
//     if (!editingBank || editingBank.ifscCode !== formData.ifscCode) {
//       const isValid = await validateBankDetails();
//       if (!isValid) return;
//     }
    
//     // Call parent's save function
//     onSave(formData);
//   };

//   return (
//     <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
//       <h2 className="text-xl font-semibold text-gray-800 mb-6">
//         {editingBank ? 'Edit Bank Account' : 'Add New Bank Account'}
//       </h2>
      
//       {/* Status Messages */}
//       {success && (
//         <div className="mb-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//             <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//           </svg>
//           <span>{success}</span>
//         </div>
//       )}

//       {/* Add/Edit Form */}
//       <form onSubmit={handleSubmit} className="space-y-6">
//         <div className="space-y-4">
//           <div>
//             <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700">
//               Account Holder Name *
//             </label>
//             <input
//               type="text"
//               name="accountHolderName"
//               id="accountHolderName"
//               value={formData.accountHolderName}
//               onChange={handleInputChange}
//               className={`mt-1 block w-full border ${errors.accountHolderName ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
//             />
//             {errors.accountHolderName && (
//               <p className="mt-1 text-sm text-red-600">{errors.accountHolderName}</p>
//             )}
//           </div>

//           <div>
//             <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
//               Bank Name *
//             </label>
//             <input
//               type="text"
//               name="bankName"
//               id="bankName"
//               list="bankList"
//               value={formData.bankName}
//               onChange={handleInputChange}
//               className={`mt-1 block w-full border ${errors.bankName ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
//             />
//             <datalist id="bankList">
//               {bankList.map((bank, index) => (
//                 <option key={index} value={bank} />
//               ))}
//             </datalist>
//             {errors.bankName && (
//               <p className="mt-1 text-sm text-red-600">{errors.bankName}</p>
//             )}
//           </div>

//           <div>
//             <label htmlFor="branch" className="block text-sm font-medium text-gray-700">
//               Branch
//             </label>
//             <input
//               type="text"
//               name="branch"
//               id="branch"
//               value={formData.branch}
//               onChange={handleInputChange}
//               className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//             />
//           </div>

//           <div>
//             <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
//               Account Number *
//             </label>
//             <input
//               type="text"
//               name="accountNumber"
//               id="accountNumber"
//               value={formData.accountNumber}
//               onChange={handleInputChange}
//               className={`mt-1 block w-full border ${errors.accountNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
//             />
//             {errors.accountNumber && (
//               <p className="mt-1 text-sm text-red-600">{errors.accountNumber}</p>
//             )}
//           </div>

//           <div className="flex items-end gap-2">
//             <div className="flex-1">
//               <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
//                 IFSC Code *
//               </label>
//               <input
//                 type="text"
//                 name="ifscCode"
//                 id="ifscCode"
//                 value={formData.ifscCode}
//                 onChange={handleInputChange}
//                 className={`mt-1 block w-full border ${errors.ifscCode ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
//                 placeholder="e.g., SBIN0000001"
//               />
//               {errors.ifscCode && (
//                 <p className="mt-1 text-sm text-red-600">{errors.ifscCode}</p>
//               )}
//               {ifscData && (
//                 <p className="mt-1 text-xs text-green-600">
//                   Verified: {ifscData.BANK} ({ifscData.BRANCH})
//                 </p>
//               )}
//             </div>
//             <button
//               type="button"
//               onClick={validateBankDetails}
//               disabled={validating || !formData.ifscCode}
//               className="h-10 py-2 px-3 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               {validating ? (
//                 <svg className="animate-spin h-4 w-4 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                 </svg>
//               ) : (
//                 'Validate'
//               )}
//             </button>
//           </div>
//         </div>

//         <div className="flex items-center">
//           <input
//             id="isDefault"
//             name="isDefault"
//             type="checkbox"
//             checked={formData.isDefault}
//             onChange={handleInputChange}
//             className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
//           />
//           <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
//             Set as default bank account
//           </label>
//         </div>

//         <div className="flex space-x-3 pt-4 border-t border-gray-200">
//           <button
//             type="button"
//             onClick={onCancel}
//             className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//           >
//             Cancel
//           </button>
//           <button
//             type="submit"
//             disabled={loading}
//             className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-75"
//           >
//             {loading ? (
//               <>
//                 <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 极 24 24">
//                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                 </svg>
//                 Processing...
//               </>
//             ) : editingBank ? 'Update' : 'Add Account'}
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// };

// const MyBankAccount = () => {
//   const [bankDetails, setBankDetails] = useState([]);
//   const [selectedBank, setSelectedBank] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [isAdding, setIsAdding] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');
//   const [viewMode, setViewMode] = useState(false);
//   const [validating, setValidating] = useState(false);
//   const [bankList, setBankList] = useState([]);
//   const [branchList, setBranchList] = useState([]);
//   const [searchingBranches, setSearchingBranches] = useState(false);

//   // Form state
//   const [formData, setFormData] = useState({
//     accountHolderName: '',
//     bankName: '',
//     accountNumber: '',
//     ifscCode: '',
//     branch: '',
//     isDefault: false
//   });

//   const token = Cookies.get("ecom_user_token");

//   // API configuration
//   const api = axios.create({
//     baseURL: process.env.REACT_APP_BACKEND_URL,
//     headers: {
//       'Authorization': `Bearer ${token}`
//     }
//   });

//   // Fetch bank details
//   const fetchBankDetails = async () => {
//     try {
//       setLoading(true);
//       const response = await api.get('api/buyer/bank');
//       setBankDetails(response.data.bankDetails || []);
//       setError('');
//     } catch (err) {
//       setError('Failed to fetch bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBankDetails();
//     // Load popular banks list for autocomplete
//     setBankList([
//       'State Bank of India',
//       'HDFC Bank',
//       'ICICI Bank',
//       'Axis Bank',
//       'Kotak Mahindra Bank',
//       'Bank of Baroda',
//       'Punjab National Bank',
//       'Canara Bank',
//       'Yes Bank',
//       'IDBI Bank'
//     ]);
//   }, []);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });

//     // If bank name changes, clear branch and IFSC
//     if (name === 'bankName') {
//       setFormData(prev => ({
//         ...prev,
//         branch: '',
//         ifscCode: ''
//       }));
//       setBranchList([]);
//     }

//     // If branch changes, clear IFSC
//     if (name === 'branch') {
//       setFormData(prev => ({
//         ...prev,
//         ifscCode: ''
//       }));
//     }
//   };

//   // Reset form
//   const resetForm = () => {
//     setFormData({
//       accountHolderName: '',
//       bankName: '',
//       accountNumber: '',
//       ifscCode: '',
//       branch: '',
//       isDefault: false
//     });
//     setSelectedBank(null);
//     setIsEditing(false);
//     setIsAdding(false);
//     setViewMode(false);
//     setError('');
//     setSuccess('');
//     setBranchList([]);
//   };

//   // Fetch branches for selected bank
//   const fetchBranches = async () => {
//     if (!formData.bankName) return;
    
//     try {
//       setSearchingBranches(true);
//       // Get bank code from bank name (simplified mapping)
//       const bankCodeMap = {
//         'state bank of india': 'SBIN',
//         'hdfc bank': 'HDFC',
//         'icici bank': 'ICIC',
//         'axis bank': 'UTIB',
//         'kotak mahindra bank': 'KKBK',
//         'bank of baroda': 'BARB',
//         'punjab national bank': 'PUNB',
//         'canara bank': 'CNRB',
//         'yes bank': 'YESB',
//         'idbi bank': 'IBKL'
//       };
      
//       const bankCode = bankCodeMap[formData.bankName.toLowerCase()];
      
//       if (!bankCode) {
//         setError('Bank not found in our database. Please enter IFSC manually.');
//         return;
//       }
      
//       // Fetch branches from Razorpay API
//       const response = await axios.get(`https://ifsc.razorpay.com/${bankCode}`);
      
//       if (response.data && Array.isArray(response.data)) {
//         const branches = response.data
//           .map(branch => branch.branch)
//           .filter(branch => branch); // Remove empty values
          
//         setBranchList([...new Set(branches)]); // Remove duplicates
//       }
//     } catch (err) {
//       console.error('Error fetching branches:', err);
//       setError('Could not fetch branches. Please enter IFSC manually.');
//     } finally {
//       setSearchingBranches(false);
//     }
//   };

//   // Validate bank details using IFSC code
//   const validateBankDetails = async () => {
//     if (!formData.ifscCode) {
//       setError('Please enter IFSC code to validate');
//       return false;
//     }

//     try {
//       setValidating(true);
//       const response = await axios.get(`https://ifsc.razorpay.com/${formData.ifscCode}`);
      
//       if (response.status === 200 && response.data) {
//         const bankData = response.data;
        
//         // Check if bank name matches
//         if (formData.bankName && 
//             bankData.BANK && 
//             !bankData.BANK.toLowerCase().includes(formData.bankName.toLowerCase())) {
//           setError(`IFSC code belongs to ${bankData.BANK}, not ${formData.bankName}`);
//           return false;
//         }
        
//         // Check if branch matches
//         if (formData.branch && 
//             bankData.BRANCH && 
//             !bankData.BRANCH.toLowerCase().includes(formData.branch.toLowerCase())) {
//           setError(`IFSC code belongs to ${bankData.BRANCH} branch, not ${formData.branch}`);
//           return false;
//         }
        
//         // If we have bank name in form, update it with the official name from API
//         if (!formData.bankName && bankData.BANK) {
//           setFormData(prev => ({ ...prev, bankName: bankData.BANK }));
//         }
        
//         // If we have branch in form, update it with the official name from API
//         if (!formData.branch && bankData.BRANCH) {
//           setFormData(prev => ({ ...prev, branch: bankData.BRANCH }));
//         }
        
//         setSuccess('Bank details validated successfully!');
//         return true;
//       }
//     } catch (err) {
//       console.error('Error validating IFSC:', err);
//       if (err.response && err.response.status === 404) {
//         setError('Invalid IFSC code. Please check and try again.');
//       } else {
//         setError('Failed to validate IFSC code. Please try again.');
//       }
//       return false;
//     } finally {
//       setValidating(false);
//     }
//   };

//   // Add new bank details
//   const handleAddBank = async (formData) => {
//     try {
//       setLoading(true);
//       await api.post('api/buyer/add-bank-details', formData);
//       setSuccess('Bank details added successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to add bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Update bank details
//   const handleUpdateBank = async (formData) => {
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${selectedBank._id}`, formData);
//       setSuccess('Bank details updated successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to update bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Set bank as default
//   const setAsDefault = async (bankId) => {
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${bankId}`, { isDefault: true });
//       setSuccess('Default bank updated successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to set default bank');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete bank account
//   const deleteBankAccount = async (bankId) => {
//     if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
//     try {
//       setLoading(true);
//       await api.delete(`api/buyer/delete-bank/${bankId}`);
//       setSuccess('Bank account deleted successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to delete bank account');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Prepare form for editing
//   const setupEditForm = (bank) => {
//     setSelectedBank(bank);
//     setIsEditing(true);
//     setIsAdding(false);
//     setViewMode(false);
//   };

//   // View bank details
//   const viewBankDetails = (bank) => {
//     setSelectedBank(bank);
//     setViewMode(true);
//     setIsEditing(false);
//     setIsAdding(false);
//   };

//   // Start adding new bank
//   const startAdding = () => {
//     resetForm();
//     setIsAdding(true);
//     setIsEditing(false);
//     setViewMode(false);
//   };

//   // Format account number for display (mask most digits)
//   const formatAccountNumber = (accNumber) => {
//     if (!accNumber) return '';
//     const lastFour = accNumber.slice(-4);
//     return `****${lastFour}`;
//   };

//   // Bank logos mapping
//   const bankLogos = {
//     'hdfc': 'https://static.vecteezy.com/system/resources/previews/020/336/703/original/hdfc-logo-hdfc-icon-free-free-vector.jpg',
//     'icici': 'https://d1i7580riw15wg.cloudfront.net/assets/newui/icici-9d1aebfd6e6615de405d5f1ed8abfa9974fa6b700c571ad75a2d8287efa853b0.webp',
//     'sbi': 'https://www.freepnglogos.com/uploads/sbi-logo-png/sbi-logo-sbi-symbol-meaning-history-and-evolution-11.png',
//     'axis': 'https://1.bp.blogspot.com/-TmSdLBZ1pJI/YCp88SdAmoI/AAAAAAAAB5g/dt1dTYBHVtkTRnM9JsSSWWkcFJ9myLf7wCLcBGAsYHQ/w1200-h630-p-k-no-nu/AXIS+BANK+PNG.png',
//     'kotak': 'https://tse2.mm.bing.net/th/id/OIP.6UU_ux7jC5nANk53xhpu0wHaE8?r=0&w=600&h=400&rs=1&pid=ImgDetMain&o=7&rm=3',
//     'yes': 'https://bfsi.eletsonline.com/wp-content/uploads/2023/05/YES-BANK.jpg',
//     'pnb': 'https://static.vecteezy.com/system/resources/previews/020/336/282/non_2x/punjab-national-bank-pnb-bank-logo-free-free-vector.jpg',
//     'bob': 'https://www.clipartmax.com/png/middle/215-2155663_bank-of-baroda-vector-logo-bank-of-baroda-logo-clipart.png',
//     'canara': 'https://www.liblogo.com/img-logo/ca8792c86d-canara-bank-logo-canara-bank-launches-qualified-institutional-placement.png',
//     'idbi': 'https://www.companieshistory.com/wp-content/uploads/2013/07/IDBI-Bank.png',
//     'default': 'https://static.vecteezy.com/system/resources/previews/013/948/672/large_2x/bank-icon-logo-design-vector.jpg'
//   };

//   // Get bank logo based on bank name
//   const getBankLogo = (bankName) => {
//     if (!bankName) return bankLogos.default;
    
//     const bankKey = bankName.toLowerCase();
//     for (const [key, logo] of Object.entries(bankLogos)) {
//       if (bankKey.includes(key)) return logo;
//     }
    
//     return bankLogos.default;
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
//       <div className="max-w-5xl mx-auto">
//         <div className="text-center mb-10">
//           <h1 className="text-4xl font-bold text-gray-900 mb-3">My Bank Accounts</h1>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Manage your bank accounts for seamless transactions. Your financial details are securely encrypted.
//           </p>
//         </div>

//         {/* Status Messages */}
//         {error && (
//           <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
//             </svg>
//             <span>{error}</span>
//           </div>
//         )}
//         {success && (
//           <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//             </svg>
//             <span>{success}</span>
//           </div>
//         )}

//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Bank Accounts List */}
//           <div className="lg:col-span-2">
//             <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
//               <div className="p-6 border-b border-gray-200">
//                 <div className="flex justify-between items-center mb-6">
//                   <h2 className="text-xl font-semibold text-gray-800">Your Bank Accounts</h2>
//                   <button
//                     onClick={startAdding}
//                     className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center shadow-md"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="极 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M10 5a1 1 极 011 1v3极3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                     </svg>
//                     Add New Bank
//                   </button>
//                 </div>

//                 {loading && !isAdding && !isEditing ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
//                   </div>
//                 ) : bankDetails.length === 0 ? (
//                   <div className="text-center py-12 bg-gray-50 rounded-xl">
//                     <div className="bg-indigo-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
//                       <svg className="h-8 w-8 text-indigo-极" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
//                       </svg>
//                     </div>
//                     <h3 className="mt-4 text-lg font-medium text-gray-900">No bank accounts</h3>
//                     <p className="mt-1 text-sm text-gray-500">Get started by adding your first bank account.</p>
//                     <div className="mt-6">
//                       <button
//                         onClick={startAdding}
//                         className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         + Add Bank Account
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="space-y-4">
//                     {bankDetails.map((bank) => (
//                       <div 
//                         key={bank._id} 
//                         className={`p-5 border rounded-xl flex flex-col md:flex-row md:items-center justify-between transition-all duration-200 hover:shadow-md cursor-pointer ${bank.isDefault ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
//                         onClick={() => viewBankDetails(bank)}
//                       >
//                         <div className="flex-1 flex items-center">
//                           <div className="bg-white p-2 rounded-xl mr-4 shadow-sm border flex items-center justify-center w-12极12">
//                             <img 
//                               src={getBankLogo(bank.bankName)} 
//                               alt={bank.bankName}
//                               className="max-h-8 max-w-8 object-contain"
//                               onError={(e) => {
//                                 e.target.onerror = null;
//                                 e.target.src = bankLogos.default;
//                               }}
//                             />
//                           </div>
//                           <div>
//                             <h3 className="font-medium text-gray-900">{bank.bankName}</h3>
//                             <p className="text-sm text-gray-500">{formatAccountNumber(bank.accountNumber)} • {bank.accountHolderName}</p>
//                             {bank.isDefault && (
//                               <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
//                                 Default
//                               </span>
//                             )}
//                           </div>
//                         </div>
//                         <div className="mt-4 md:mt-0 flex space-x-3">
//                           {!bank.isDefault && (
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 setAsDefault(bank._id);
//                               }}
//                               className="text-sm text-indigo-600 hover:text-indigo-900 font-medium flex items-center"
//                             >
//                               <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                               </svg>
//                               Set Default
//                             </button>
//                           )}
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setupEditForm(bank);
//                             }}
//                             className="极 sm text-gray-600 hover:text-gray-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
//                             </svg>
//                             Edit
//                           </button>
//                           {/* <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               deleteBankAccount(bank._id);
//                             }}
//                             className="text-sm text-red-600 hover:text-red-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1极6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
//                             </svg>
//                             Delete
//                           </button> */}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Side Panel for Add/Edit/View */}
//           <div className="lg:col-span-1">
//             {(isAdding || isEditing) && (
//               <BankAccountForm
//                 editingBank={isEditing ? selectedBank : null}
//                 onSave={isEditing ? handleUpdateBank : handleAddBank}
//                 onCancel={resetForm}
//                 loading={loading}
//                 token={token}
//               />
//             )}

//             {/* Information Card */}
//             {!isAdding && !isEditing && !viewMode && (
//               <div className="bg-white rounded-2xl shadow-lg p-6">
//                 <h3 className="text-lg font-semib极 text-gray-800 mb-4 flex items-center">
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
//                   </svg>
//                   Secure Banking
//                 </h3>
//                 <p className="text-gray-600 text-sm">
//                   Your bank details are encrypted and stored securely. We never share your financial information with third parties.
//                 </p>
//                 <div className="mt-4 pt-4 border-t border-gray-100">
//                   <div className="flex items-center text-sm text-gray-500 mb-2">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.极 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 极.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     Bank-level encryption
//                   </div>
//                   <div className="flex items-center text-sm text-gray-500">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     PCI DSS compliant
//                   </div>
//                   <div className="flex items-center text-sm text-gray-500 mt-2">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 极 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
//                     </svg>
//                     IFSC validation with Razorpay API
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MyBankAccount;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import BankAccountForm from './Component/BankAccountForm';
// const MyBankAccount = () => {
//   const [bankDetails, setBankDetails] = useState([]);
//   const [selectedBank, setSelectedBank] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [isAdding, setIsAdding] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');
//   const [viewMode, setViewMode] = useState(false);
//   const [validating, setValidating] = useState(false);
//   const [bankList, setBankList] = useState([]);
//   const [branchList, setBranchList] = useState([]);
//   const [searchingBranches, setSearchingBranches] = useState(false);

//   // Form state
//   const [formData, setFormData] = useState({
//     accountHolderName: '',
//     bankName: '',
//     accountNumber: '',
//     ifscCode: '',
//     branch: '',
//     isDefault: false
//   });

//   const token = Cookies.get("ecom_user_token");

//   // API configuration
//   const api = axios.create({
//     baseURL: process.env.REACT_APP_BACKEND_URL,
//     headers: {
//       'Authorization': `Bearer ${token}`
//     }
//   });

//   // Fetch bank details
//   const fetchBankDetails = async () => {
//     try {
//       setLoading(true);
//       const response = await api.get('api/buyer/bank');
//       setBankDetails(response.data.bankDetails || []);
//       setError('');
//     } catch (err) {
//       setError('Failed to fetch bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBankDetails();
//     // Load popular banks list for autocomplete
//     setBankList([
//       'State Bank of India',
//       'HDFC Bank',
//       'ICICI Bank',
//       'Axis Bank',
//       'Kotak Mahindra Bank',
//       'Bank of Baroda',
//       'Punjab National Bank',
//       'Canara Bank',
//       'Yes Bank',
//       'IDBI Bank'
//     ]);
//   }, []);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });

//     // If bank name changes, clear branch and IFSC
//     if (name === 'bankName') {
//       setFormData(prev => ({
//         ...prev,
//         branch: '',
//         ifscCode: ''
//       }));
//       setBranchList([]);
//     }

//     // If branch changes, clear IFSC
//     if (name === 'branch') {
//       setFormData(prev => ({
//         ...prev,
//         ifscCode: ''
//       }));
//     }
//   };

//   // Reset form
//   const resetForm = () => {
//     setFormData({
//       accountHolderName: '',
//       bankName: '',
//       accountNumber: '',
//       ifscCode: '',
//       branch: '',
//       isDefault: false
//     });
//     setSelectedBank(null);
//     setIsEditing(false);
//     setIsAdding(false);
//     setViewMode(false);
//     setError('');
//     setSuccess('');
//     setBranchList([]);
//   };

//   // Fetch branches for selected bank
//   const fetchBranches = async () => {
//     if (!formData.bankName) return;
    
//     try {
//       setSearchingBranches(true);
//       // Get bank code from bank name (simplified mapping)
//       const bankCodeMap = {
//         'state bank of india': 'SBIN',
//         'hdfc bank': 'HDFC',
//         'icici bank': 'ICIC',
//         'axis bank': 'UTIB',
//         'kotak mahindra bank': 'KKBK',
//         'bank of baroda': 'BARB',
//         'punjab national bank': 'PUNB',
//         'canara bank': 'CNRB',
//         'yes bank': 'YESB',
//         'idbi bank': 'IBKL'
//       };
      
//       const bankCode = bankCodeMap[formData.bankName.toLowerCase()];
      
//       if (!bankCode) {
//         setError('Bank not found in our database. Please enter IFSC manually.');
//         return;
//       }
      
//       // Fetch branches from Razorpay API
//       const response = await axios.get(`https://ifsc.razorpay.com/${bankCode}`);
      
//       if (response.data && Array.isArray(response.data)) {
//         const branches = response.data
//           .map(branch => branch.branch)
//           .filter(branch => branch); // Remove empty values
          
//         setBranchList([...new Set(branches)]); // Remove duplicates
//       }
//     } catch (err) {
//       console.error('Error fetching branches:', err);
//       setError('Could not fetch branches. Please enter IFSC manually.');
//     } finally {
//       setSearchingBranches(false);
//     }
//   };

//   // Validate bank details using IFSC code
//   const validateBankDetails = async () => {
//     if (!formData.ifscCode) {
//       setError('Please enter IFSC code to validate');
//       return false;
//     }

//     try {
//       setValidating(true);
//       const response = await axios.get(`https://ifsc.razorpay.com/${formData.ifscCode}`);
      
//       if (response.status === 200 && response.data) {
//         const bankData = response.data;
        
//         // Check if bank name matches
//         if (formData.bankName && 
//             bankData.BANK && 
//             !bankData.BANK.toLowerCase().includes(formData.bankName.toLowerCase())) {
//           setError(`IFSC code belongs to ${bankData.BANK}, not ${formData.bankName}`);
//           return false;
//         }
        
//         // Check if branch matches
//         if (formData.branch && 
//             bankData.BRANCH && 
//             !bankData.BRANCH.toLowerCase().includes(formData.branch.toLowerCase())) {
//           setError(`IFSC code belongs to ${bankData.BRANCH} branch, not ${formData.branch}`);
//           return false;
//         }
        
//         // If we have bank name in form, update it with the official name from API
//         if (!formData.bankName && bankData.BANK) {
//           setFormData(prev => ({ ...prev, bankName: bankData.BANK }));
//         }
        
//         // If we have branch in form, update it with the official name from API
//         if (!formData.branch && bankData.BRANCH) {
//           setFormData(prev => ({ ...prev, branch: bankData.BRANCH }));
//         }
        
//         setSuccess('Bank details validated successfully!');
//         return true;
//       }
//     } catch (err) {
//       console.error('Error validating IFSC:', err);
//       if (err.response && err.response.status === 404) {
//         setError('Invalid IFSC code. Please check and try again.');
//       } else {
//         setError('Failed to validate IFSC code. Please try again.');
//       }
//       return false;
//     } finally {
//       setValidating(false);
//     }
//   };

//   // Add new bank details
//   const handleAddBank = async (e) => {
//     e.preventDefault();
    
//     // Validate bank details first
//     const isValid = await validateBankDetails();
//     if (!isValid) return;
    
//     try {
//       setLoading(true);
//       await api.post('api/buyer/add-bank-details', formData);
//       setSuccess('Bank details added successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to add bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Update bank details
//   const handleUpdateBank = async (e) => {
//     e.preventDefault();
    
//     // Validate bank details first if IFSC was changed
//     if (formData.ifscCode !== selectedBank.ifscCode) {
//       const isValid = await validateBankDetails();
//       if (!isValid) return;
//     }
    
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${selectedBank._id}`, formData);
//       setSuccess('Bank details updated successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to update bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Set bank as default
//   const setAsDefault = async (bankId) => {
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${bankId}`, { isDefault: true });
//       setSuccess('Default bank updated successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to set default bank');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete bank account
//   const deleteBankAccount = async (bankId) => {
//     if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
//     try {
//       setLoading(true);
//       await api.delete(`api/buyer/delete-bank/${bankId}`);
//       setSuccess('Bank account deleted successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to delete bank account');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Prepare form for editing
//   const setupEditForm = (bank) => {
//     setSelectedBank(bank);
//     setFormData({
//       accountHolderName: bank.accountHolderName,
//       bankName: bank.bankName,
//       accountNumber: bank.accountNumber,
//       ifscCode: bank.ifscCode,
//       branch: bank.branch || '',
//       isDefault: bank.isDefault
//     });
//     setIsEditing(true);
//     setIsAdding(false);
//     setViewMode(false);
//   };

//   // View bank details
//   const viewBankDetails = (bank) => {
//     setSelectedBank(bank);
//     setViewMode(true);
//     setIsEditing(false);
//     setIsAdding(false);
//   };

//   // Start adding new bank
//   const startAdding = () => {
//     resetForm();
//     setIsAdding(true);
//     setIsEditing(false);
//     setViewMode(false);
//   };

//   // Format account number for display (mask most digits)
//   const formatAccountNumber = (accNumber) => {
//     if (!accNumber) return '';
//     const lastFour = accNumber.slice(-4);
//     return `****${lastFour}`;
//   };

//   // Bank logos mapping
//   const bankLogos = {
//     'hdfc': 'https://static.vecteezy.com/system/resources/previews/020/336/703/original/hdfc-logo-hdfc-icon-free-free-vector.jpg',
//     'icici': 'https://d1i7580riw15wg.cloudfront.net/assets/newui/icici-9d1aebfd6e6615de405d5f1ed8abfa9974fa6b700c571ad75a2d8287efa853b0.webp',
//     'sbi': 'https://www.freepnglogos.com/uploads/sbi-logo-png/sbi-logo-sbi-symbol-meaning-history-and-evolution-11.png',
//     'axis': 'https://1.bp.blogspot.com/-TmSdLBZ1pJI/YCp88SdAmoI/AAAAAAAAB5g/dt1dTYBHVtkTRnM9JsSSWWkcFJ9myLf7wCLcBGAsYHQ/w1200-h630-p-k-no-nu/AXIS+BANK+PNG.png',
//     'kotak': 'https://tse2.mm.bing.net/th/id/OIP.6UU_ux7jC5nANk53xhpu0wHaE8?r=0&w=600&h=400&rs=1&pid=ImgDetMain&o=7&rm=3',
//     'yes': 'https://bfsi.eletsonline.com/wp-content/uploads/2023/05/YES-BANK.jpg',
//     'pnb': 'https://static.vecteezy.com/system/resources/previews/020/336/282/non_2x/punjab-national-bank-pnb-bank-logo-free-free-vector.jpg',
//     'bob': 'https://www.clipartmax.com/png/middle/215-2155663_bank-of-baroda-vector-logo-bank-of-baroda-logo-clipart.png',
//     'canara': 'https://www.liblogo.com/img-logo/ca8792c86d-canara-bank-logo-canara-bank-launches-qualified-institutional-placement.png',
//     'idbi': 'https://www.companieshistory.com/wp-content/uploads/2013/07/IDBI-Bank.png',
//     'default': 'https://static.vecteezy.com/system/resources/previews/013/948/672/large_2x/bank-icon-logo-design-vector.jpg'
//   };

//   // Get bank logo based on bank name
//   const getBankLogo = (bankName) => {
//     if (!bankName) return bankLogos.default;
    
//     const bankKey = bankName.toLowerCase();
//     for (const [key, logo] of Object.entries(bankLogos)) {
//       if (bankKey.includes(key)) return logo;
//     }
    
//     return bankLogos.default;
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
//       <div className="max-w-5xl mx-auto">
//         <div className="text-center mb-10">
//           <h1 className="text-4xl font-bold text-gray-900 mb-3">My Bank Accounts</h1>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Manage your bank accounts for seamless transactions. Your financial details are securely encrypted.
//           </p>
//         </div>

//         {/* Status Messages */}
//         {error && (
//           <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
//             </svg>
//             <span>{error}</span>
//           </div>
//         )}
//         {success && (
//           <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//             </svg>
//             <span>{success}</span>
//           </div>
//         )}

//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Bank Accounts List */}
//           <div className="lg:col-span-2">
//             <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
//               <div className="p-6 border-b border-gray-200">
//                 <div className="flex justify-between items-center mb-6">
//                   <h2 className="text-xl font-semibold text-gray-800">Your Bank Accounts</h2>
//                   <button
//                     onClick={startAdding}
//                     className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center shadow-md"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                     </svg>
//                     Add New Bank
//                   </button>
//                 </div>

//                 {loading && !isAdding && !isEditing ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
//                   </div>
//                 ) : bankDetails.length === 0 ? (
//                   <div className="text-center py-12 bg-gray-50 rounded-xl">
//                     <div className="bg-indigo-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
//                       <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
//                       </svg>
//                     </div>
//                     <h3 className="mt-4 text-lg font-medium text-gray-900">No bank accounts</h3>
//                     <p className="mt-1 text-sm text-gray-500">Get started by adding your first bank account.</p>
//                     <div className="mt-6">
//                       <button
//                         onClick={startAdding}
//                         className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         + Add Bank Account
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="space-y-4">
//                     {bankDetails.map((bank) => (
//                       <div 
//                         key={bank._id} 
//                         className={`p-5 border rounded-xl flex flex-col md:flex-row md:items-center justify-between transition-all duration-200 hover:shadow-md cursor-pointer ${bank.isDefault ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
//                         onClick={() => viewBankDetails(bank)}
//                       >
//                         <div className="flex-1 flex items-center">
//                           <div className="bg-white p-2 rounded-xl mr-4 shadow-sm border flex items-center justify-center w-12 h-12">
//                             <img 
//                               src={getBankLogo(bank.bankName)} 
//                               alt={bank.bankName}
//                               className="max-h-8 max-w-8 object-contain"
//                               onError={(e) => {
//                                 e.target.onerror = null;
//                                 e.target.src = bankLogos.default;
//                               }}
//                             />
//                           </div>
//                           <div>
//                             <h3 className="font-medium text-gray-900">{bank.bankName}</h3>
//                             <p className="text-sm text-gray-500">{formatAccountNumber(bank.accountNumber)} • {bank.accountHolderName}</p>
//                             {bank.isDefault && (
//                               <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
//                                 Default
//                               </span>
//                             )}
//                           </div>
//                         </div>
//                         <div className="mt-4 md:mt-0 flex space-x-3">
//                           {!bank.isDefault && (
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 setAsDefault(bank._id);
//                               }}
//                               className="text-sm text-indigo-600 hover:text-indigo-900 font-medium flex items-center"
//                             >
//                               <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                               </svg>
//                               Set Default
//                             </button>
//                           )}
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setupEditForm(bank);
//                             }}
//                             className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
//                             </svg>
//                             Edit
//                           </button>
//                           {/* <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               deleteBankAccount(bank._id);
//                             }}
//                             className="text-sm text-red-600 hover:text-red-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
//                             </svg>
//                             Delete
//                           </button> */}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Side Panel for Add/Edit/View */}
//           <div className="lg:col-span-1">
//             {(isAdding || isEditing) && (
//   <BankAccountForm
//     editingBank={isEditing ? selectedBank : null}
//     onSave={isEditing ? handleUpdateBank : handleAddBank}
//     onCancel={resetForm}
//     loading={loading}
//     token={token}
//   />
// )}

//             {/* Information Card */}
//             {!isAdding && !isEditing && !viewMode && (
//               <div className="bg-white rounded-2xl shadow-lg p-6">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
//                   </svg>
//                   Secure Banking
//                 </h3>
//                 <p className="text-gray-600 text-sm">
//                   Your bank details are encrypted and stored securely. We never share your financial information with third parties.
//                 </p>
//                 <div className="mt-4 pt-4 border-t border-gray-100">
//                   <div className="flex items-center text-sm text-gray-500 mb-2">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     Bank-level encryption
//                   </div>
//                   <div className="flex items-center text-sm text-gray-500">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     PCI DSS compliant
//                   </div>
//                   <div className="flex items-center text-sm text-gray-500 mt-2">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
//                     </svg>
//                     IFSC validation with Razorpay API
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MyBankAccount;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';

// const MyBankAccount = () => {
//   const [bankDetails, setBankDetails] = useState([]);
//   const [selectedBank, setSelectedBank] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [isAdding, setIsAdding] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');
//   const [viewMode, setViewMode] = useState(false);

//   // Form state
//   const [formData, setFormData] = useState({
//     accountHolderName: '',
//     bankName: '',
//     accountNumber: '',
//     ifscCode: '',
//     branch: '',
//     isDefault: false
//   });

//   const token = Cookies.get("ecom_user_token");

//   // API configuration
//   const api = axios.create({
//     baseURL: process.env.REACT_APP_BACKEND_URL,
//     headers: {
//       'Authorization': `Bearer ${token}`
//     }
//   });

//   // Fetch bank details
//   const fetchBankDetails = async () => {
//     try {
//       setLoading(true);
//       const response = await api.get('api/buyer/bank');
//       setBankDetails(response.data.bankDetails || []);
//       setError('');
//     } catch (err) {
//       setError('Failed to fetch bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBankDetails();
//   }, []);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });
//   };

//   // Reset form
//   const resetForm = () => {
//     setFormData({
//       accountHolderName: '',
//       bankName: '',
//       accountNumber: '',
//       ifscCode: '',
//       branch: '',
//       isDefault: false
//     });
//     setSelectedBank(null);
//     setIsEditing(false);
//     setIsAdding(false);
//     setViewMode(false);
//     setError('');
//     setSuccess('');
//   };

//   // Add new bank details
//   const handleAddBank = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
//       await api.post('api/buyer/add-bank-details', formData);
//       setSuccess('Bank details added successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to add bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Update bank details
//   const handleUpdateBank = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${selectedBank._id}`, formData);
//       setSuccess('Bank details updated successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to update bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Set bank as default
//   const setAsDefault = async (bankId) => {
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${bankId}`, { isDefault: true });
//       setSuccess('Default bank updated successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to set default bank');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete bank account
//   const deleteBankAccount = async (bankId) => {
//     if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
//     try {
//       setLoading(true);
//       await api.delete(`api/buyer/delete-bank/${bankId}`);
//       setSuccess('Bank account deleted successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to delete bank account');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Prepare form for editing
//   const setupEditForm = (bank) => {
//     setSelectedBank(bank);
//     setFormData({
//       accountHolderName: bank.accountHolderName,
//       bankName: bank.bankName,
//       accountNumber: bank.accountNumber,
//       ifscCode: bank.ifscCode,
//       branch: bank.branch || '',
//       isDefault: bank.isDefault
//     });
//     setIsEditing(true);
//     setIsAdding(false);
//     setViewMode(false);
//   };

//   // View bank details
//   const viewBankDetails = (bank) => {
//     setSelectedBank(bank);
//     setViewMode(true);
//     setIsEditing(false);
//     setIsAdding(false);
//   };

//   // Start adding new bank
//   const startAdding = () => {
//     resetForm();
//     setIsAdding(true);
//     setIsEditing(false);
//     setViewMode(false);
//   };

//   // Format account number for display (mask most digits)
//   const formatAccountNumber = (accNumber) => {
//     if (!accNumber) return '';
//     const lastFour = accNumber.slice(-4);
//     return `****${lastFour}`;
//   };

//   // Bank logos mapping
//   const bankLogos = {
//     'hdfc': 'https://static.vecteezy.com/system/resources/previews/020/336/703/original/hdfc-logo-hdfc-icon-free-free-vector.jpg',
//     'icici': 'https://d1i7580riw15wg.cloudfront.net/assets/newui/icici-9d1aebfd6e6615de405d5f1ed8abfa9974fa6b700c571ad75a2d8287efa853b0.webp',
//     'sbi': 'https://www.freepnglogos.com/uploads/sbi-logo-png/sbi-logo-sbi-symbol-meaning-history-and-evolution-11.png',
//     'axis': 'https://1.bp.blogspot.com/-TmSdLBZ1pJI/YCp88SdAmoI/AAAAAAAAB5g/dt1dTYBHVtkTRnM9JsSSWWkcFJ9myLf7wCLcBGAsYHQ/w1200-h630-p-k-no-nu/AXIS+BANK+PNG.png',
//     'kotak': 'https://tse2.mm.bing.net/th/id/OIP.6UU_ux7jC5nANk53xhpu0wHaE8?r=0&w=600&h=400&rs=1&pid=ImgDetMain&o=7&rm=3',
//     'yes': 'https://bfsi.eletsonline.com/wp-content/uploads/2023/05/YES-BANK.jpg',
//     'pnb': 'https://static.vecteezy.com/system/resources/previews/020/336/282/non_2x/punjab-national-bank-pnb-bank-logo-free-free-vector.jpg',
//     'bob': 'https://www.clipartmax.com/png/middle/215-2155663_bank-of-baroda-vector-logo-bank-of-baroda-logo-clipart.png',
//     'canara': 'https://www.liblogo.com/img-logo/ca8792c86d-canara-bank-logo-canara-bank-launches-qualified-institutional-placement.png',
//     'idbi': 'https://www.companieshistory.com/wp-content/uploads/2013/07/IDBI-Bank.png',
//     'default': 'https://static.vecteezy.com/system/resources/previews/013/948/672/large_2x/bank-icon-logo-design-vector.jpg'
//   };

//   // Get bank logo based on bank name
//   const getBankLogo = (bankName) => {
//     if (!bankName) return bankLogos.default;
    
//     const bankKey = bankName.toLowerCase();
//     for (const [key, logo] of Object.entries(bankLogos)) {
//       if (bankKey.includes(key)) return logo;
//     }
    
//     return bankLogos.default;
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
//       <div className="max-w-5xl mx-auto">
//         <div className="text-center mb-10">
//           <h1 className="text-4xl font-bold text-gray-900 mb-3">My Bank Accounts</h1>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Manage your bank accounts for seamless transactions. Your financial details are securely encrypted.
//           </p>
//         </div>

//         {/* Status Messages */}
//         {error && (
//           <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
//             </svg>
//             <span>{error}</span>
//           </div>
//         )}
//         {success && (
//           <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//             </svg>
//             <span>{success}</span>
//           </div>
//         )}

//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Bank Accounts List */}
//           <div className="lg:col-span-2">
//             <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
//               <div className="p-6 border-b border-gray-200">
//                 <div className="flex justify-between items-center mb-6">
//                   <h2 className="text-xl font-semibold text-gray-800">Your Bank Accounts</h2>
//                   <button
//                     onClick={startAdding}
//                     className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center shadow-md"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                     </svg>
//                     Add New Bank
//                   </button>
//                 </div>

//                 {loading && !isAdding && !isEditing ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
//                   </div>
//                 ) : bankDetails.length === 0 ? (
//                   <div className="text-center py-12 bg-gray-50 rounded-xl">
//                     <div className="bg-indigo-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
//                       <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
//                       </svg>
//                     </div>
//                     <h3 className="mt-4 text-lg font-medium text-gray-900">No bank accounts</h3>
//                     <p className="mt-1 text-sm text-gray-500">Get started by adding your first bank account.</p>
//                     <div className="mt-6">
//                       <button
//                         onClick={startAdding}
//                         className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         + Add Bank Account
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="space-y-4">
//                     {bankDetails.map((bank) => (
//                       <div 
//                         key={bank._id} 
//                         className={`p-5 border rounded-xl flex flex-col md:flex-row md:items-center justify-between transition-all duration-200 hover:shadow-md cursor-pointer ${bank.isDefault ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
//                         onClick={() => viewBankDetails(bank)}
//                       >
//                         <div className="flex-1 flex items-center">
//                           <div className="bg-white p-2 rounded-xl mr-4 shadow-sm border flex items-center justify-center w-12 h-12">
//                             <img 
//                               src={getBankLogo(bank.bankName)} 
//                               alt={bank.bankName}
//                               className="max-h-8 max-w-8 object-contain"
//                               onError={(e) => {
//                                 e.target.onerror = null;
//                                 e.target.src = bankLogos.default;
//                               }}
//                             />
//                           </div>
//                           <div>
//                             <h3 className="font-medium text-gray-900">{bank.bankName}</h3>
//                             <p className="text-sm text-gray-500">{formatAccountNumber(bank.accountNumber)} • {bank.accountHolderName}</p>
//                             {bank.isDefault && (
//                               <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
//                                 Default
//                               </span>
//                             )}
//                           </div>
//                         </div>
//                         <div className="mt-4 md:mt-0 flex space-x-3">
//                           {!bank.isDefault && (
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 setAsDefault(bank._id);
//                               }}
//                               className="text-sm text-indigo-600 hover:text-indigo-900 font-medium flex items-center"
//                             >
//                               <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                               </svg>
//                               Set Default
//                             </button>
//                           )}
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setupEditForm(bank);
//                             }}
//                             className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
//                             </svg>
//                             Edit
//                           </button>
//                           {/* <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               deleteBankAccount(bank._id);
//                             }}
//                             className="text-sm text-red-600 hover:text-red-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
//                             </svg>
//                             Delete
//                           </button> */}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Side Panel for Add/Edit/View */}
//           <div className="lg:col-span-1">
//             {(isAdding || isEditing || viewMode) && (
//               <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
//                 <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
//                   {viewMode ? (
//                     <>
//                       <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
//                         <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
//                         <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
//                       </svg>
//                       Bank Account Details
//                     </>
//                   ) : isEditing ? 'Edit Bank Account' : 'Add New Bank Account'}
//                 </h2>
                
//                 {viewMode ? (
//                   // View Mode
//                   <div className="space-y-5">
//                     <div className="flex justify-center">
//                       <div className="bg-indigo-100 p-4 rounded-xl">
//                         <img 
//                           src={getBankLogo(selectedBank.bankName)} 
//                           alt={selectedBank.bankName}
//                           className="h-16 object-contain"
//                           onError={(e) => {
//                             e.target.onerror = null;
//                             e.target.src = bankLogos.default;
//                           }}
//                         />
//                       </div>
//                     </div>
                    
//                     <div className="space-y-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Bank Name</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.bankName}</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Account Holder Name</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.accountHolderName}</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Account Number</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.accountNumber}</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">IFSC Code</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.ifscCode}</p>
//                       </div>
                      
//                       {selectedBank.branch && (
//                         <div>
//                           <label className="block text-sm font-medium text-gray-500">Branch</label>
//                           <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.branch}</p>
//                         </div>
//                       )}
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Default Account</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">
//                           {selectedBank.isDefault ? 'Yes' : 'No'}
//                         </p>
//                       </div>
//                     </div>
                    
//                     <div className="pt-4 flex space-x-3 border-t border-gray-200">
//                       <button
//                         onClick={() => setupEditForm(selectedBank)}
//                         className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center justify-center"
//                       >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                           <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
//                         </svg>
//                         Edit
//                       </button>
//                       <button
//                         onClick={resetForm}
//                         className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         Close
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   // Add/Edit Form
//                   <form onSubmit={isEditing ? handleUpdateBank : handleAddBank} className="space-y-6">
//                     <div className="space-y-4">
//                       <div>
//                         <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700">
//                           Account Holder Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="accountHolderName"
//                           id="accountHolderName"
//                           value={formData.accountHolderName}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
//                           Bank Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="bankName"
//                           id="bankName"
//                           value={formData.bankName}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
//                           Account Number *
//                         </label>
//                         <input
//                           type="text"
//                           name="accountNumber"
//                           id="accountNumber"
//                           value={formData.accountNumber}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
//                           IFSC Code *
//                         </label>
//                         <input
//                           type="text"
//                           name="ifscCode"
//                           id="ifscCode"
//                           value={formData.ifscCode}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="branch" className="block text-sm font-medium text-gray-700">
//                           Branch
//                         </label>
//                         <input
//                           type="text"
//                           name="branch"
//                           id="branch"
//                           value={formData.branch}
//                           onChange={handleInputChange}
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>
//                     </div>

//                     <div className="flex items-center">
//                       <input
//                         id="isDefault"
//                         name="isDefault"
//                         type="checkbox"
//                         checked={formData.isDefault}
//                         onChange={handleInputChange}
//                         className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
//                       />
//                       <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
//                         Set as default bank account
//                       </label>
//                     </div>

//                     <div className="flex space-x-3 pt-4 border-t border-gray-200">
//                       <button
//                         type="button"
//                         onClick={resetForm}
//                         className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         Cancel
//                       </button>
//                       <button
//                         type="submit"
//                         disabled={loading}
//                         className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-75"
//                       >
//                         {loading ? (
//                           <>
//                             <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                             </svg>
//                             Processing...
//                           </>
//                         ) : isEditing ? 'Update' : 'Add Account'}
//                       </button>
//                     </div>
//                   </form>
//                 )}
//               </div>
//             )}

//             {/* Information Card */}
//             {!isAdding && !isEditing && !viewMode && (
//               <div className="bg-white rounded-2xl shadow-lg p-6">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
//                   </svg>
//                   Secure Banking
//                 </h3>
//                 <p className="text-gray-600 text-sm">
//                   Your bank details are encrypted and stored securely. We never share your financial information with third parties.
//                 </p>
//                 <div className="mt-4 pt-4 border-t border-gray-100">
//                   <div className="flex items-center text-sm text-gray-500 mb-2">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     Bank-level encryption
//                   </div>
//                   <div className="flex items-center text-sm text-gray-500">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     PCI DSS compliant
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MyBankAccount;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';

// const MyBankAccount = () => {
//   const [bankDetails, setBankDetails] = useState([]);
//   const [selectedBank, setSelectedBank] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [isAdding, setIsAdding] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');
//   const [viewMode, setViewMode] = useState(false); // New state for view mode

//   // Form state
//   const [formData, setFormData] = useState({
//     accountHolderName: '',
//     bankName: '',
//     accountNumber: '',
//     ifscCode: '',
//     branch: '',
//     isDefault: false
//   });

//   const token = Cookies.get("ecom_user_token");

//   // API configuration
//   const api = axios.create({
//     baseURL: process.env.REACT_APP_BACKEND_URL,
//     headers: {
//       'Authorization': `Bearer ${token}`
//     }
//   });

//   // Fetch bank details
//   const fetchBankDetails = async () => {
//     try {
//       setLoading(true);
//       const response = await api.get('api/buyer/bank');
//       setBankDetails(response.data.bankDetails || []);
//       setError('');
//     } catch (err) {
//       setError('Failed to fetch bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBankDetails();
//   }, []);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });
//   };

//   // Reset form
//   const resetForm = () => {
//     setFormData({
//       accountHolderName: '',
//       bankName: '',
//       accountNumber: '',
//       ifscCode: '',
//       branch: '',
//       isDefault: false
//     });
//     setSelectedBank(null);
//     setIsEditing(false);
//     setIsAdding(false);
//     setViewMode(false);
//     setError('');
//     setSuccess('');
//   };

//   // Add new bank details
//   const handleAddBank = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
//       await api.post('api/buyer/add-bank-details', formData);
//       setSuccess('Bank details added successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to add bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Update bank details
//   const handleUpdateBank = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${selectedBank._id}`, formData);
//       setSuccess('Bank details updated successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to update bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Set bank as default
//   const setAsDefault = async (bankId) => {
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${bankId}`, { isDefault: true });
//       setSuccess('Default bank updated successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to set default bank');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete bank account
//   const deleteBankAccount = async (bankId) => {
//     if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    
//     try {
//       setLoading(true);
//       await api.delete(`api/buyer/delete-bank/${bankId}`);
//       setSuccess('Bank account deleted successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to delete bank account');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Prepare form for editing
//   const setupEditForm = (bank) => {
//     setSelectedBank(bank);
//     setFormData({
//       accountHolderName: bank.accountHolderName,
//       bankName: bank.bankName,
//       accountNumber: bank.accountNumber,
//       ifscCode: bank.ifscCode,
//       branch: bank.branch || '',
//       isDefault: bank.isDefault
//     });
//     setIsEditing(true);
//     setIsAdding(false);
//     setViewMode(false);
//   };

//   // View bank details
//   const viewBankDetails = (bank) => {
//     setSelectedBank(bank);
//     setViewMode(true);
//     setIsEditing(false);
//     setIsAdding(false);
//   };

//   // Start adding new bank
//   const startAdding = () => {
//     resetForm();
//     setIsAdding(true);
//     setIsEditing(false);
//     setViewMode(false);
//   };

//   // Format account number for display (mask most digits)
//   const formatAccountNumber = (accNumber) => {
//     if (!accNumber) return '';
//     const lastFour = accNumber.slice(-4);
//     return `****${lastFour}`;
//   };

//   // Get bank logo based on bank name (simulated)
//   const getBankLogo = (bankName) => {
//     const bankLogos = {
//       'hdfc': '🏦',
//       'icici': '🏢',
//       'sbi': '🇮🇳',
//       'axis': '🔄',
//       'kotak': '🔶',
//       'yes': '✅'
//     };
    
//     const bankKey = bankName.toLowerCase();
//     for (const [key, logo] of Object.entries(bankLogos)) {
//       if (bankKey.includes(key)) return logo;
//     }
    
//     return '🏦'; // Default bank logo
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4 sm:px-6 lg:px-8">
//       <div className="max-w-5xl mx-auto">
//         <div className="text-center mb-10">
//           <h1 className="text-4xl font-bold text-gray-900 mb-3">My Bank Accounts</h1>
//           <p className="text-lg text-gray-600 max-w-2xl mx-auto">
//             Manage your bank accounts for seamless transactions. Your financial details are securely encrypted.
//           </p>
//         </div>

//         {/* Status Messages */}
//         {error && (
//           <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
//             </svg>
//             <span>{error}</span>
//           </div>
//         )}
//         {success && (
//           <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex items-center">
//             <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
//               <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//             </svg>
//             <span>{success}</span>
//           </div>
//         )}

//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
//           {/* Bank Accounts List */}
//           <div className="lg:col-span-2">
//             <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
//               <div className="p-6 border-b border-gray-200">
//                 <div className="flex justify-between items-center mb-6">
//                   <h2 className="text-xl font-semibold text-gray-800">Your Bank Accounts</h2>
//                   <button
//                     onClick={startAdding}
//                     className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center shadow-md"
//                   >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                     </svg>
//                     Add New Bank
//                   </button>
//                 </div>

//                 {loading && !isAdding && !isEditing ? (
//                   <div className="flex justify-center py-8">
//                     <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
//                   </div>
//                 ) : bankDetails.length === 0 ? (
//                   <div className="text-center py-12 bg-gray-50 rounded-xl">
//                     <div className="bg-indigo-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
//                       <svg className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
//                       </svg>
//                     </div>
//                     <h3 className="mt-4 text-lg font-medium text-gray-900">No bank accounts</h3>
//                     <p className="mt-1 text-sm text-gray-500">Get started by adding your first bank account.</p>
//                     <div className="mt-6">
//                       <button
//                         onClick={startAdding}
//                         className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         + Add Bank Account
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   <div className="space-y-4">
//                     {bankDetails.map((bank) => (
//                       <div 
//                         key={bank._id} 
//                         className={`p-5 border rounded-xl flex flex-col md:flex-row md:items-center justify-between transition-all duration-200 hover:shadow-md cursor-pointer ${bank.isDefault ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}
//                         onClick={() => viewBankDetails(bank)}
//                       >
//                         <div className="flex-1 flex items-center">
//                           <div className="bg-white p-3 rounded-xl mr-4 shadow-sm border">
//                             <span className="text-2xl">{getBankLogo(bank.bankName)}</span>
//                           </div>
//                           <div>
//                             <h3 className="font-medium text-gray-900">{bank.bankName}</h3>
//                             <p className="text-sm text-gray-500">{formatAccountNumber(bank.accountNumber)} • {bank.accountHolderName}</p>
//                             {bank.isDefault && (
//                               <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
//                                 Default
//                               </span>
//                             )}
//                           </div>
//                         </div>
//                         <div className="mt-4 md:mt-0 flex space-x-3">
//                           {!bank.isDefault && (
//                             <button
//                               onClick={(e) => {
//                                 e.stopPropagation();
//                                 setAsDefault(bank._id);
//                               }}
//                               className="text-sm text-indigo-600 hover:text-indigo-900 font-medium flex items-center"
//                             >
//                               <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                                 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                               </svg>
//                               Set Default
//                             </button>
//                           )}
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               setupEditForm(bank);
//                             }}
//                             className="text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
//                             </svg>
//                             Edit
//                           </button>
//                           {/* <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               deleteBankAccount(bank._id);
//                             }}
//                             className="text-sm text-red-600 hover:text-red-900 font-medium flex items-center"
//                           >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                               <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
//                             </svg>
//                             Delete
//                           </button> */}
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Side Panel for Add/Edit/View */}
//           <div className="lg:col-span-1">
//             {(isAdding || isEditing || viewMode) && (
//               <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
//                 <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
//                   {viewMode ? (
//                     <>
//                       <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
//                         <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
//                         <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
//                       </svg>
//                       Bank Account Details
//                     </>
//                   ) : isEditing ? 'Edit Bank Account' : 'Add New Bank Account'}
//                 </h2>
                
//                 {viewMode ? (
//                   // View Mode
//                   <div className="space-y-5">
//                     <div className="flex justify-center">
//                       <div className="bg-indigo-100 p-5 rounded-xl">
//                         <span className="text-4xl">{getBankLogo(selectedBank.bankName)}</span>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-4">
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Bank Name</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.bankName}</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Account Holder Name</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.accountHolderName}</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Account Number</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.accountNumber}</p>
//                       </div>
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">IFSC Code</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.ifscCode}</p>
//                       </div>
                      
//                       {selectedBank.branch && (
//                         <div>
//                           <label className="block text-sm font-medium text-gray-500">Branch</label>
//                           <p className="mt-1 text-lg font-medium text-gray-900">{selectedBank.branch}</p>
//                         </div>
//                       )}
                      
//                       <div>
//                         <label className="block text-sm font-medium text-gray-500">Default Account</label>
//                         <p className="mt-1 text-lg font-medium text-gray-900">
//                           {selectedBank.isDefault ? 'Yes' : 'No'}
//                         </p>
//                       </div>
//                     </div>
                    
//                     <div className="pt-4 flex space-x-3 border-t border-gray-200">
//                       <button
//                         onClick={() => setupEditForm(selectedBank)}
//                         className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition duration-300 flex items-center justify-center"
//                       >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
//                           <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
//                         </svg>
//                         Edit
//                       </button>
//                       <button
//                         onClick={resetForm}
//                         className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         Close
//                       </button>
//                     </div>
//                   </div>
//                 ) : (
//                   // Add/Edit Form
//                   <form onSubmit={isEditing ? handleUpdateBank : handleAddBank} className="space-y-6">
//                     <div className="space-y-4">
//                       <div>
//                         <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700">
//                           Account Holder Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="accountHolderName"
//                           id="accountHolderName"
//                           value={formData.accountHolderName}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
//                           Bank Name *
//                         </label>
//                         <input
//                           type="text"
//                           name="bankName"
//                           id="bankName"
//                           value={formData.bankName}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
//                           Account Number *
//                         </label>
//                         <input
//                           type="text"
//                           name="accountNumber"
//                           id="accountNumber"
//                           value={formData.accountNumber}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
//                           IFSC Code *
//                         </label>
//                         <input
//                           type="text"
//                           name="ifscCode"
//                           id="ifscCode"
//                           value={formData.ifscCode}
//                           onChange={handleInputChange}
//                           required
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>

//                       <div>
//                         <label htmlFor="branch" className="block text-sm font-medium text-gray-700">
//                           Branch
//                         </label>
//                         <input
//                           type="text"
//                           name="branch"
//                           id="branch"
//                           value={formData.branch}
//                           onChange={handleInputChange}
//                           className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                         />
//                       </div>
//                     </div>

//                     <div className="flex items-center">
//                       <input
//                         id="isDefault"
//                         name="isDefault"
//                         type="checkbox"
//                         checked={formData.isDefault}
//                         onChange={handleInputChange}
//                         className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
//                       />
//                       <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
//                         Set as default bank account
//                       </label>
//                     </div>

//                     <div className="flex space-x-3 pt-4 border-t border-gray-200">
//                       <button
//                         type="button"
//                         onClick={resetForm}
//                         className="flex-1 bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                       >
//                         Cancel
//                       </button>
//                       <button
//                         type="submit"
//                         disabled={loading}
//                         className="flex-1 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-75"
//                       >
//                         {loading ? (
//                           <>
//                             <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                             </svg>
//                             Processing...
//                           </>
//                         ) : isEditing ? 'Update' : 'Add Account'}
//                       </button>
//                     </div>
//                   </form>
//                 )}
//               </div>
//             )}

//             {/* Information Card */}
//             {!isAdding && !isEditing && !viewMode && (
//               <div className="bg-white rounded-2xl shadow-lg p-6">
//                 <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
//                   <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
//                     <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
//                   </svg>
//                   Secure Banking
//                 </h3>
//                 <p className="text-gray-600 text-sm">
//                   Your bank details are encrypted and stored securely. We never share your financial information with third parties.
//                 </p>
//                 <div className="mt-4 pt-4 border-t border-gray-100">
//                   <div className="flex items-center text-sm text-gray-500 mb-2">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     Bank-level encryption
//                   </div>
//                   <div className="flex items-center text-sm text-gray-500">
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
//                       <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
//                     </svg>
//                     PCI DSS compliant
//                   </div>
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MyBankAccount;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';

// const MyBankAccount = () => {
//   const [bankDetails, setBankDetails] = useState([]);
//   const [selectedBank, setSelectedBank] = useState(null);
//   const [isEditing, setIsEditing] = useState(false);
//   const [isAdding, setIsAdding] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [success, setSuccess] = useState('');

//   // Form state
//   const [formData, setFormData] = useState({
//     accountHolderName: '',
//     bankName: '',
//     accountNumber: '',
//     ifscCode: '',
//     branch: '',
//     upiId: '',
//     isDefault: false
//   });

//   const token = Cookies.get("ecom_user_token");

//   // API configuration
//   const api = axios.create({
//     baseURL: process.env.REACT_APP_BACKEND_URL,
//     headers: {
//       'Authorization': `Bearer ${token}`
//     }
//   });

//   // Fetch bank details
//   const fetchBankDetails = async () => {
//     try {
//       setLoading(true);
//       const response = await api.get('api/buyer/bank');
//       setBankDetails(response.data.bankDetails || []);
//       setError('');
//     } catch (err) {
//       setError('Failed to fetch bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBankDetails();
//   }, []);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });
//   };

//   // Reset form
//   const resetForm = () => {
//     setFormData({
//       accountHolderName: '',
//       bankName: '',
//       accountNumber: '',
//       ifscCode: '',
//       branch: '',
//       upiId: '',
//       isDefault: false
//     });
//     setSelectedBank(null);
//     setIsEditing(false);
//     setIsAdding(false);
//     setError('');
//     setSuccess('');
//   };

//   // Add new bank details
//   const handleAddBank = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
//       await api.post('api/buyer/add-bank-details', formData);
//       setSuccess('Bank details added successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to add bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Update bank details
//   const handleUpdateBank = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${selectedBank._id}`, formData);
//       setSuccess('Bank details updated successfully');
//       resetForm();
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to update bank details');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Set bank as default
//   const setAsDefault = async (bankId) => {
//     try {
//       setLoading(true);
//       await api.patch(`api/buyer/update-bank/${bankId}`, { isDefault: true });
//       setSuccess('Default bank updated successfully');
//       fetchBankDetails();
//     } catch (err) {
//       setError(err.response?.data?.message || 'Failed to set default bank');
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Prepare form for editing
//   const setupEditForm = (bank) => {
//     setSelectedBank(bank);
//     setFormData({
//       accountHolderName: bank.accountHolderName,
//       bankName: bank.bankName,
//       accountNumber: bank.accountNumber,
//       ifscCode: bank.ifscCode,
//       branch: bank.branch || '',
//       upiId: bank.upiId || '',
//       isDefault: bank.isDefault
//     });
//     setIsEditing(true);
//     setIsAdding(false);
//   };

//   // Start adding new bank
//   const startAdding = () => {
//     resetForm();
//     setIsAdding(true);
//     setIsEditing(false);
//   };

//   // Format account number for display (mask most digits)
//   const formatAccountNumber = (accNumber) => {
//     if (!accNumber) return '';
//     const lastFour = accNumber.slice(-4);
//     return `****${lastFour}`;
//   };

//   return (
//     <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//       <div className="max-w-4xl mx-auto">
//         <div className="text-center mb-8">
//           <h1 className="text-3xl font-bold text-gray-900">My Bank Accounts</h1>
//           <p className="mt-2 text-lg text-gray-600">Manage your bank details for payments and withdrawals</p>
//         </div>

//         {/* Status Messages */}
//         {error && (
//           <div className="mb-6 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
//             <p>{error}</p>
//           </div>
//         )}
//         {success && (
//           <div className="mb-6 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md">
//             <p>{success}</p>
//           </div>
//         )}

//         <div className="bg-white shadow-xl rounded-lg overflow-hidden">
//           {/* Bank Accounts List */}
//           <div className="p-6 border-b border-gray-200">
//             <div className="flex justify-between items-center mb-6">
//               <h2 className="text-xl font-semibold text-gray-800">Your Bank Accounts</h2>
//               <button
//                 onClick={startAdding}
//                 className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-md transition duration-300"
//               >
//                 + Add New Bank
//               </button>
//             </div>

//             {loading && !isAdding && !isEditing ? (
//               <div className="flex justify-center py-8">
//                 <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
//               </div>
//             ) : bankDetails.length === 0 ? (
//               <div className="text-center py-8 bg-gray-50 rounded-lg">
//                 <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-6 0H5m2 0h4M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
//                 </svg>
//                 <h3 className="mt-2 text-sm font-medium text-gray-900">No bank accounts</h3>
//                 <p className="mt-1 text-sm text-gray-500">Get started by adding your first bank account.</p>
//                 <div className="mt-6">
//                   <button
//                     onClick={startAdding}
//                     className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                   >
//                     + Add Bank Account
//                   </button>
//                 </div>
//               </div>
//             ) : (
//               <div className="space-y-4">
//                 {bankDetails.map((bank) => (
//                   <div key={bank._id} className={`p-4 border rounded-lg flex flex-col md:flex-row md:items-center justify-between ${bank.isDefault ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'}`}>
//                     <div className="flex-1">
//                       <div className="flex items-center">
//                         <div className="bg-indigo-100 p-3 rounded-full mr-4">
//                           <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
//                           </svg>
//                         </div>
//                         <div>
//                           <h3 className="font-medium text-gray-900">{bank.bankName}</h3>
//                           <p className="text-sm text-gray-500">{formatAccountNumber(bank.accountNumber)} • {bank.accountHolderName}</p>
//                           {bank.isDefault && (
//                             <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
//                               Default
//                             </span>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                     <div className="mt-4 md:mt-0 flex space-x-2">
//                       {!bank.isDefault && (
//                         <button
//                           onClick={() => setAsDefault(bank._id)}
//                           className="text-sm text-indigo-600 hover:text-indigo-900 font-medium"
//                         >
//                           Set Default
//                         </button>
//                       )}
//                       <button
//                         onClick={() => setupEditForm(bank)}
//                         className="text-sm text-gray-600 hover:text-gray-900 font-medium"
//                       >
//                         Edit
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {/* Add/Edit Form */}
//           {(isAdding || isEditing) && (
//             <div className="p-6 bg-gray-50 border-t border-gray-200">
//               <h2 className="text-xl font-semibold text-gray-800 mb-6">
//                 {isEditing ? 'Edit Bank Account' : 'Add New Bank Account'}
//               </h2>
              
//               <form onSubmit={isEditing ? handleUpdateBank : handleAddBank} className="space-y-6">
//                 <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
//                   <div>
//                     <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700">
//                       Account Holder Name *
//                     </label>
//                     <input
//                       type="text"
//                       name="accountHolderName"
//                       id="accountHolderName"
//                       value={formData.accountHolderName}
//                       onChange={handleInputChange}
//                       required
//                       className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="bankName" className="block text-sm font-medium text-gray-700">
//                       Bank Name *
//                     </label>
//                     <input
//                       type="text"
//                       name="bankName"
//                       id="bankName"
//                       value={formData.bankName}
//                       onChange={handleInputChange}
//                       required
//                       className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700">
//                       Account Number *
//                     </label>
//                     <input
//                       type="text"
//                       name="accountNumber"
//                       id="accountNumber"
//                       value={formData.accountNumber}
//                       onChange={handleInputChange}
//                       required
//                       className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700">
//                       IFSC Code *
//                     </label>
//                     <input
//                       type="text"
//                       name="ifscCode"
//                       id="ifscCode"
//                       value={formData.ifscCode}
//                       onChange={handleInputChange}
//                       required
//                       className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="branch" className="block text-sm font-medium text-gray-700">
//                       Branch
//                     </label>
//                     <input
//                       type="text"
//                       name="branch"
//                       id="branch"
//                       value={formData.branch}
//                       onChange={handleInputChange}
//                       className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="upiId" className="block text-sm font-medium text-gray-700">
//                       UPI ID
//                     </label>
//                     <input
//                       type="text"
//                       name="upiId"
//                       id="upiId"
//                       value={formData.upiId}
//                       onChange={handleInputChange}
//                       className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                     />
//                   </div>
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     id="isDefault"
//                     name="isDefault"
//                     type="checkbox"
//                     checked={formData.isDefault}
//                     onChange={handleInputChange}
//                     className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
//                   />
//                   <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900">
//                     Set as default bank account
//                   </label>
//                 </div>

//                 <div className="flex justify-end space-x-3">
//                   <button
//                     type="button"
//                     onClick={resetForm}
//                     className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={loading}
//                     className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-75"
//                   >
//                     {loading ? (
//                       <>
//                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                         Processing...
//                       </>
//                     ) : isEditing ? 'Update Bank Account' : 'Add Bank Account'}
//                   </button>
//                 </div>
//               </form>
//             </div>
//           )}
//         </div>

//         {/* Information Box */}
//         <div className="mt-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
//           <div className="flex">
//             <div className="flex-shrink-0">
//               <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
//                 <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
//               </svg>
//             </div>
//             <div className="ml-3">
//               <h3 className="text-sm font-medium text-blue-800">Secure Banking</h3>
//               <div className="mt-2 text-sm text-blue-700">
//                 <p>Your bank details are encrypted and stored securely. We never share your financial information with third parties.</p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MyBankAccount;